import { useEffect, useRef, useState, useCallback } from 'react'
import { format, isSameDay, isAfter } from 'date-fns'
import { toast } from 'react-toastify'

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768

/**
 * Manages check-in/check-out date state for both desktop (DateRangePicker)
 * and mobile (react-datepicker) with a single source of truth.
 */
export const useDateRange = (initialCheckin = null, initialCheckout = null) => {
  const [startDate, setStartDate] = useState(
    initialCheckin ? new Date(initialCheckin) : null,
  )
  const [endDate, setEndDate] = useState(
    initialCheckout ? new Date(initialCheckout) : null,
  )
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [activeInput, setActiveInput] = useState(null)

  const calendarRef = useRef(null)
  const mobileCalendarRef = useRef(null)
  const dateInputRef = useRef(null)

  const isDateSelected = !!(startDate && endDate)

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDatePicker) return

    const handleClickOutside = (event) => {
      const inDesktop =
        calendarRef.current && calendarRef.current.contains(event.target)
      const inMobile =
        mobileCalendarRef.current &&
        mobileCalendarRef.current.contains(event.target)
      const inInput =
        dateInputRef.current && dateInputRef.current.contains(event.target)

      if (!inDesktop && !inMobile && !inInput) {
        setShowDatePicker(false)
        setActiveInput(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDatePicker])

  // Lock body scroll when mobile date picker is open
  useEffect(() => {
    if (showDatePicker && isMobile()) {
      document.body.classList.add('bottom-sheet-open')
    } else {
      document.body.classList.remove('bottom-sheet-open')
    }
    return () => document.body.classList.remove('bottom-sheet-open')
  }, [showDatePicker])

  const toggleDatePicker = useCallback(() => {
    setShowDatePicker((prev) => {
      if (!prev) {
        // Opening — decide which input to focus
        if (!startDate) setActiveInput('checkin')
        else if (!endDate) setActiveInput('checkout')
        else setActiveInput('checkin')
      } else {
        setActiveInput(null)
      }
      return !prev
    })
  }, [startDate, endDate])

  const closeDatePicker = useCallback(() => {
    setShowDatePicker(false)
    setActiveInput(null)
  }, [])

  /** Mobile: user picks check-in */
  const handleStartDateChange = useCallback(
    (date) => {
      setStartDate(date)
      if (endDate && date > endDate) setEndDate(null)
      setActiveInput('checkout')
    },
    [endDate],
  )

  /** Mobile: user picks check-out */
  const handleEndDateChange = useCallback(
    (date) => {
      if (startDate && !isAfter(date, startDate)) {
        setStartDate(date)
        setEndDate(null)
        setActiveInput('checkout')
        return false
      }

      setEndDate(date)
      closeDatePicker()
      return true
    },
    [closeDatePicker, startDate],
  )

  /** Desktop DateRangePicker onChange */
  const handleDesktopDateChange = useCallback(
    (item) => {
      const { startDate: newStart, endDate: newEnd } = item.selection

      // Both dates already selected → start fresh
      if (startDate && endDate) {
        setStartDate(newStart)
        setEndDate(null)
        setActiveInput('checkout')
        return false
      }

      // No start → set start only
      if (!startDate) {
        setStartDate(newStart)
        setEndDate(null)
        setActiveInput('checkout')
        return false
      }

      // Start exists, no end → complete the range unless user picked same/earlier day,
      // which should restart check-in selection.
      if (startDate && !endDate && newEnd) {
        if (!isAfter(newEnd, startDate)) {
          setStartDate(newStart)
          setEndDate(null)
          setActiveInput('checkout')
          return false
        }

        setEndDate(newEnd)
        closeDatePicker()
        return true
      }

      // Fallback: complete range in one selection
      if (newStart && newEnd && newStart.getTime() !== newEnd.getTime()) {
        setStartDate(newStart)
        setEndDate(newEnd)
        closeDatePicker()
        return true
      } else {
        setStartDate(newStart)
        setEndDate(null)
        setActiveInput('checkout')
        return false
      }
    },
    [closeDatePicker, endDate, startDate],
  )

  /** Build the dateRange array that DateRangePicker expects */
  const dateRange = [
    {
      startDate: startDate || new Date(),
      endDate: endDate || startDate || new Date(),
      key: 'selection',
    },
  ]

  /** Validate dates before search — returns true if valid */
  const validateDates = useCallback(() => {
    const hasStart = !!startDate
    const hasEnd = !!endDate

    if (hasStart !== hasEnd) {
      toast.warn('Please select both check-in and check-out dates')
      return false
    }

    if (hasStart && hasEnd) {
      if (isSameDay(startDate, endDate)) {
        toast.warn('Check-in and check-out dates cannot be the same')
        return false
      }
      if (isAfter(startDate, endDate)) {
        toast.warn('Check-out date must be after check-in date')
        return false
      }
    }

    return true
  }, [startDate, endDate])

  /** Clear both dates */
  const clearDates = useCallback(() => {
    setStartDate(null)
    setEndDate(null)
    closeDatePicker()
  }, [closeDatePicker])

  /** Format dates for URL params */
  const getFormattedDates = useCallback(() => {
    if (!startDate || !endDate) return null
    return {
      checkin: format(startDate, 'yyyy-MM-dd'),
      checkout: format(endDate, 'yyyy-MM-dd'),
    }
  }, [startDate, endDate])

  /** Display string for the search bar */
  const displayText = isDateSelected
    ? `${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd')}`
    : null

  return {
    startDate,
    endDate,
    isDateSelected,
    showDatePicker,
    activeInput,
    calendarRef,
    mobileCalendarRef,
    dateInputRef,
    dateRange,
    displayText,
    toggleDatePicker,
    closeDatePicker,
    handleStartDateChange,
    handleEndDateChange,
    handleDesktopDateChange,
    validateDates,
    getFormattedDates,
    clearDates,
  }
}
